import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Home from "@/pages/Home";
import ScrollToHash from "@/components/ScrollToHash";
import { Toaster } from "sonner";
import { CustomerAuthProvider } from "@/lib/customerAuth";

// Lazy-load everything except Home so the first paint of the marketing site
// only downloads the code it actually needs (drops the initial bundle by
// ~150-200 KB — admin, blog, service/location pages, and OAuth callbacks
// are all fetched on demand).
const AdminLogin           = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard       = lazy(() => import("@/pages/AdminDashboard"));
const AdminForgotPassword  = lazy(() => import("@/pages/AdminForgotPassword"));
const AdminResetPassword   = lazy(() => import("@/pages/AdminResetPassword"));
const ManageBooking        = lazy(() => import("@/pages/ManageBooking"));
const Privacy              = lazy(() => import("@/pages/Privacy"));
const Terms                = lazy(() => import("@/pages/Terms"));
const ServicesIndex        = lazy(() => import("@/pages/ServicesIndex"));
const ServiceDetail        = lazy(() => import("@/pages/ServiceDetail"));
const LocationsIndex       = lazy(() => import("@/pages/LocationsIndex"));
const LocationDetail       = lazy(() => import("@/pages/LocationDetail"));
const BlogIndex            = lazy(() => import("@/pages/BlogIndex"));
const BlogPost             = lazy(() => import("@/pages/BlogPost"));
const AccountPage          = lazy(() => import("@/pages/AccountPage"));
const AuthCallback         = lazy(() => import("@/pages/AuthCallback"));
const HalifaxLanding       = lazy(() => import("@/pages/HalifaxLanding"));
const FallLanding          = lazy(() => import("@/pages/FallLanding"));

// Simple full-viewport dark loading state — matches app theme, no layout shift.
const RouteFallback = () => (
  <div className="min-h-screen bg-[#0A0A0C]" aria-hidden />
);

/**
 * Intercepts the OAuth callback hash BEFORE normal routing runs.
 * If we detect `#session_id=` in the URL, we mount <AuthCallback /> instead
 * of matching a normal route — this prevents the ProtectedRoute race condition
 * where /me is called before the session cookie is established.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/halifax-detailing" element={<HalifaxLanding />} />
        <Route path="/fall-detailing" element={<FallLanding />} />
        <Route path="/book" element={<HalifaxLanding />} />
        <Route path="/services" element={<ServicesIndex />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/service-areas" element={<LocationsIndex />} />
        <Route path="/service-areas/:slug" element={<LocationDetail />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot" element={<AdminForgotPassword />} />
        <Route path="/admin/reset/:token" element={<AdminResetPassword />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/manage/:token" element={<ManageBooking />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Only apply Lenis smooth scroll on the marketing site, not admin routes
    if (typeof window !== "undefined" && (window.location.pathname.startsWith("/admin") || window.location.pathname.startsWith("/manage"))) {
      return;
    }
    // Disable Lenis entirely on touch devices — it hijacks native touch events
    // and breaks drag interactions (e.g. Before/After slider) on mobile.
    const isTouchDevice =
      typeof window !== "undefined" &&
      (("ontouchstart" in window) ||
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
        window.matchMedia("(pointer: coarse)").matches);
    if (isTouchDevice) {
      return;
    }
    // Dynamic import so the ~5 KB Lenis lib is fetched only when actually used
    // (desktop, non-admin routes). Mobile and admin skip the download entirely.
    let cancelled = false;
    let cleanup = null;
    import("@studio-freight/lenis").then(({ default: Lenis }) => {
      if (cancelled) return;
      const lenis = new Lenis({
        duration: 1.15,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        syncTouch: false,
      });
      if (typeof window !== "undefined") window.__lenis = lenis;
      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);
      cleanup = () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    });
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <CustomerAuthProvider>
            <ScrollToHash />
            <AppRouter />
          </CustomerAuthProvider>
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "#0A0A0C",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#F2F2F2",
            },
          }}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;
